import { runPageCleanup } from './globals.js';
import { openConfirmModal as ensureUiModalBundle } from './ui.js';

void ensureUiModalBundle;

const SITE_NAME = 'subseq.bio';
const DEFAULT_TITLE = 'Subseq.bio - Protein Design Jobs';
const FONT_PREVIEW_STACK_KEY = 'ssq_font_preview_stack';

function applyStoredFontPreview() {
  try {
    const stack = localStorage.getItem(FONT_PREVIEW_STACK_KEY);
    if (stack) document.documentElement.style.setProperty('--font-mono', stack);
  } catch {}
}

applyStoredFontPreview();

const routeTitles = {
  home: DEFAULT_TITLE,
  updates: `Updates | ${SITE_NAME}`,
  programs: `Programs | ${SITE_NAME}`,
  'bio-agents': `Bio Agents | ${SITE_NAME}`,
  workshop: `Workshop | ${SITE_NAME}`,
  newtask: `New Task | ${SITE_NAME}`,
  newjob: `New Task | ${SITE_NAME}`,
  jobs: `Jobs | ${SITE_NAME}`,
  pipelines: `Pipelines | ${SITE_NAME}`,
  templates: `Templates | ${SITE_NAME}`,
  viewjob: `Job Details | ${SITE_NAME}`,
  projects: `Projects | ${SITE_NAME}`,
  viewproject: `Project Details | ${SITE_NAME}`,
  datasets: `Datasets | ${SITE_NAME}`,
  newdataset: `New Dataset | ${SITE_NAME}`,
  viewdataset: `Dataset Details | ${SITE_NAME}`,
  viewdata: `Dataset Details | ${SITE_NAME}`,
  funding: `Funding & Credits | ${SITE_NAME}`,
  pricing: `Pricing | ${SITE_NAME}`,
  keys: `API Keys | ${SITE_NAME}`,
  team: `Team | ${SITE_NAME}`,
  teams: `Teams | ${SITE_NAME}`,
  test: `Font Test | ${SITE_NAME}`,
};

const pageLoaders = {
  home: () => import('./pages/home.js'),
  updates: () => import('./pages/updates.js'),
  newtask: () => import('./pages/newtask.js'),
  newjob: () => import('./pages/newtask.js'),
  jobs: () => import('./pages/jobs.js'),
  pipelines: () => import('./pages/pipelines.js'),
  templates: () => import('./pages/templates.js'),
  viewjob: () => import('./pages/viewjob.js'),
  viewdata: () => import('./pages/viewdata.js'),
  viewdataset: () => import('./pages/viewdata.js'),
  viewproject: () => import('./pages/viewproject.js'),
  projects: () => import('./pages/projects.js'),
  datasets: () => import('./pages/datasets.js'),
  newdataset: () => import('./pages/newdataset.js'),
  funding: () => import('./pages/funding.js'),
  pricing: () => import('./pages/pricing.js'),
  keys: () => import('./pages/keys.js'),
  team: () => import('./pages/team.js'),
  teams: () => import('./pages/teams.js'),
  programs: () => import('./pages/programs.js'),
  'bio-agents': () => import('./pages/bioAgents.js'),
  workshop: () => import('./pages/workshop.js'),
  test: () => import('./pages/test.js'),
};

let routeLeaveGuard = null;
let routeViewContextProvider = null;
let activeRoutePath = `${window.location.pathname}${window.location.search}`;
let renderToken = 0;
let closeJobInfoModal = null;

function setRouteLeaveGuard(fn) {
  routeLeaveGuard = typeof fn === 'function' ? fn : null;
  return () => {
    if (routeLeaveGuard === fn) {
      routeLeaveGuard = null;
    }
  };
}

function setRouteViewContextProvider(fn) {
  routeViewContextProvider = typeof fn === 'function' ? fn : null;
  return () => {
    if (routeViewContextProvider === fn) {
      routeViewContextProvider = null;
    }
  };
}

function getRouteViewContext() {
  const path = `${window.location.pathname}${window.location.search}`;
  const { page } = getRoute();
  const base = { path, page };
  if (typeof routeViewContextProvider !== 'function') {
    return base;
  }
  try {
    const extra = routeViewContextProvider();
    if (!extra || typeof extra !== 'object' || Array.isArray(extra)) {
      return base;
    }
    return { ...base, ...extra };
  } catch (_) {
    return base;
  }
}

async function canLeaveRoute(nextPath, source = 'navigate') {
  if (typeof routeLeaveGuard !== 'function') return true;
  const from = `${window.location.pathname}${window.location.search}`;
  const to = typeof nextPath === 'string' ? nextPath : from;
  if (to === from) return true;
  try {
    const result = await routeLeaveGuard({ from, to, source });
    return result !== false;
  } catch (err) {
    console.warn('route leave guard failed', err);
    return false;
  }
}

function updateDocumentTitle(page) {
  try {
    const nextTitle = routeTitles[page] || DEFAULT_TITLE;
    if (document.title !== nextTitle) {
      document.title = nextTitle;
    }
  } catch {}
}

function normalizeLegacyHash() {
  const { hash, pathname } = window.location;
  if (hash && hash.startsWith('#')) {
    const raw = hash.slice(1); // e.g. "jobs" or "viewjob?jobid=123"
    const [page, queryString] = raw.split('?');
    const path = page === 'home' ? '/' : `/${page}`;
    const qs = queryString ? `?${queryString}` : '';
    // Only rewrite if we're not already on a matching clean path
    if (pathname === '/' || pathname === '/index.html') {
      window.history.replaceState({}, document.title, `${path}${qs}`);
    }
  }
}

function getRoute() {
  // Returns { page, params }
  const { pathname, search } = window.location;
  const clean = pathname.replace(/\/+$/, ''); // trim trailing slashes
  let page = clean === '' || clean === '/' ? 'home' : clean.slice(1); // remove leading '/'
  // Only keep the first path segment (e.g., '/jobs/123' -> 'jobs') for now
  if (page.includes('/')) page = page.split('/')[0];
  if (page === 'whats-new') page = 'updates';
  const params = new URLSearchParams(search);
  return { page, params };
}

function canonicalizeRoutePath(path) {
  if (typeof path !== 'string') return path;
  try {
    const url = new URL(path, window.location.origin);
    if (url.origin === window.location.origin) {
      const pathname = url.pathname.replace(/\/+$/, '');
      if (pathname === '/newjob') return `/newtask${url.search}${url.hash}`;
      if (pathname === '/agents' || pathname === '/chat') return `/bio-agents${url.search}${url.hash}`;
    }
  } catch {}
  return path;
}

function scrollMainToTop() {
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const mc = document.getElementById('main-content');
    if (mc) {
      if (typeof mc.scrollTo === 'function') mc.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      mc.scrollTop = 0;
      mc.scrollLeft = 0;
    }
  } catch (e) {}
}

async function loadAndMountPage(page, params, token) {
  const loader = pageLoaders[page] || null;
  const pageName = loader ? page : 'home';
  const pageLoader = loader || pageLoaders.home;
  if (!loader) {
    console.error(`Page "${page}" not found`);
  }
  try {
    const mod = await pageLoader();
    if (token !== renderToken) return false;
    const mount = mod && mod.default;
    if (typeof mount !== 'function') {
      throw new Error(`Page "${pageName}" does not export a default mount function.`);
    }
    mount(loader ? params : new URLSearchParams());
    updateDocumentTitle(pageName);
    return true;
  } catch (err) {
    console.error(`Failed to load page "${pageName}":`, err);
    if (token !== renderToken) return false;
    const el = document.getElementById('main-content');
    if (el) {
      el.innerHTML = '<div class="section main-area"><div class="narrow-page"><p class="error-message">Failed to load this page.</p></div></div>';
    }
    updateDocumentTitle(pageName);
    return false;
  }
}

async function render() {
  closeJobInfoModal?.();
  closeJobInfoModal = null;
  const token = ++renderToken;
  const { page, params } = getRoute();
  routeViewContextProvider = null;
  runPageCleanup();
  if (page === 'newjob') {
    window.history.replaceState({}, '', `/newtask${window.location.search || ''}`);
    await render();
    return;
  }
  if (page === 'agents' || page === 'chat') {
    window.history.replaceState({}, '', `/bio-agents${window.location.search || ''}`);
    await render();
    return;
  }
  if (page === 'pipeline' || page === 'newpipeline') {
    const nextParams = new URLSearchParams(params);
    nextParams.set('pipeline', '1');
    nextParams.set('config', '1');
    window.history.replaceState({}, '', `/newtask?${nextParams.toString()}`);
    await render();
    return;
  }
  if (page === 'template') {
    window.history.replaceState({}, '', '/newtask?pipeline=1&config=1');
    await render();
    return;
  }
  const docRedirects = {
    apiref: '/docs/api',
    guide: '/docs',
    rfdiff: '/docs/rfdiffusion',
    esmfold: '/docs/esmfold',
    esmfold2: '/docs/esmfold2',
    esmc: '/docs/esmc',
    ligandmpnn: '/docs/ligandmpnn',
    proteinmpnn: '/docs/ligandmpnn',
    af2: '/docs/alphafold2',
    openfold3: '/docs/openfold3',
    boltz1: '/docs/boltz1',
    boltzgen: '/docs/boltzgen',
    bioemu: '/docs/bioemu',
    bioemu2: '/docs/bioemu2',
    mmseqs2: '/docs/mmseqs2',
    diffdock: '/docs/diffdock-l',
    'diffdock-pp': '/docs/diffdock-pp',
    'shape-comp': '/docs/shape-comp',
    gromacs: '/docs/gromacs',
    pymolplus: '/docs/pymolplus',
    openmm: '/docs/openmm',
    freebindcraft: '/docs/freebindcraft',
    utility: '/docs/utility',
  };
  if (docRedirects[page]) {
    window.location.href = docRedirects[page];
    return;
  }
  const mounted = await loadAndMountPage(page, params, token);
  if (token !== renderToken) return;
  if (mounted) scrollMainToTop();
  activeRoutePath = `${window.location.pathname}${window.location.search}`;
}

// Public navigation helper
async function navigate(path, options = {}) {
  if (typeof path !== 'string') return;
  if (!path.startsWith('/')) path = `/${path}`;
  path = canonicalizeRoutePath(path);
  const url = new URL(path, window.location.origin);
  if (url.origin === window.location.origin && url.pathname.replace(/\/+$/, '') === '/viewjob') {
    closeJobInfoModal?.();
    const mod = await pageLoaders.viewjob();
    closeJobInfoModal = mod.openJobInfoModal(url.searchParams, {
      onClose: () => { closeJobInfoModal = null; },
    });
    return;
  }
  const force = !!(options && options.force);
  const sameRoute = window.location.pathname + window.location.search === path;
  if (sameRoute && !force) {
    // no-op if same route
    return;
  }
  const ok = await canLeaveRoute(path, 'navigate');
  if (!ok) return;
  closeJobInfoModal?.();
  closeJobInfoModal = null;
  if (sameRoute) {
    window.history.replaceState({}, '', path);
  } else {
    window.history.pushState({}, '', path);
  }
  await render();
}

// Expose helpers globally for inline handlers
window.navigate = navigate;
window.route = render;
window.setRouteLeaveGuard = setRouteLeaveGuard;
window.setRouteViewContextProvider = setRouteViewContextProvider;
window.getRouteViewContext = getRouteViewContext;

// Intercept internal link clicks
document.addEventListener('click', (e) => {
  let anchor = null;
  const t = e.target;
  if (t && typeof t.closest === 'function') {
    anchor = t.closest('a');
  }
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href) return;
  // Ignore links that should not be router-intercepted
  // - downloads
  // - explicit new-tab targets
  // - external markers
  // - API endpoints (e.g., file downloads)
  if (
    anchor.hasAttribute('download') ||
    anchor.getAttribute('target') === '_blank' ||
    anchor.getAttribute('rel') === 'external' ||
    href.startsWith('/api/')
  ) {
    return; // allow browser default behavior
  }
  // Only intercept same-origin, root-relative links
  const url = new URL(href, window.location.origin);
  const isSameOrigin = url.origin === window.location.origin;
  const isAppLink = href.startsWith('/') && !href.startsWith('//');
  if (isSameOrigin && isAppLink) {
    // Standalone static pages served outside the SPA (full page loads)
    const isStandalone =
      url.pathname === '/docs' ||
      url.pathname === '/docs.html' ||
      url.pathname.startsWith('/docs/') ||
      url.pathname === '/contact';
    if (isStandalone) {
      return;
    }
    e.preventDefault();
    navigate(url.pathname + url.search);
  }
});

// Handle back/forward
window.addEventListener('popstate', async () => {
  const targetPath = `${window.location.pathname}${window.location.search}`;
  const prevPath = activeRoutePath;
  const ok = await canLeaveRoute(targetPath, 'popstate');
  if (!ok) {
    window.history.pushState({}, '', prevPath);
    return;
  }
  await render();
});

// Normalize legacy hash routes once, then render current route
normalizeLegacyHash();
render();
